import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, X, CheckCircle2, AlertTriangle, Link2, Unplug, RefreshCw, ExternalLink } from "lucide-react";
import { inputCls, btnPrimary, btnSecondary, relTime } from "./crm";
import { errMsg } from "./useCrm";

export const CALENDLY_STATUS = {
  connected: { label: "Connected", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  not_connected: { label: "Not Connected", cls: "border-slate-200 bg-slate-50 text-slate-600" },
  error: { label: "Connection Error", cls: "border-rose-200 bg-rose-50 text-rose-700" },
  event_type_missing: { label: "Event Type Not Configured", cls: "border-amber-200 bg-amber-50 text-amber-700" },
};

export function CalendlyBadge({ status }) {
  const m = CALENDLY_STATUS[status] || CALENDLY_STATUS.not_connected;
  return <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold ${m.cls}`} data-testid={`calendly-status-${status}`}>{m.label}</span>;
}

/** Agent-side "Calendly Integration" settings sheet (token never leaves the server once saved). */
export default function CalendlySheet({ api, onClose }) {
  const [state, setState] = useState(null);
  const [token, setToken] = useState("");
  const [types, setTypes] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setState((await api.get("/me/calendly")).data); } catch (e) { toast.error(errMsg(e, "Could not load Calendly settings.")); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const loadTypes = useCallback(async () => {
    try { setTypes((await api.get("/me/calendly/event-types")).data); } catch (e) { toast.error(errMsg(e, "Could not load your event types.")); }
  }, [api]);
  useEffect(() => { if (state?.status && state.status !== "not_connected" && types === null) loadTypes(); }, [state, types, loadTypes]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (ok) toast.success(ok); return r; } catch (e) { toast.error(errMsg(e, "Something went wrong.")); } finally { setBusy(false); }
  };
  const connect = async (e) => {
    e.preventDefault();
    const r = await run(() => api.post("/me/calendly/connect", { token: token.trim() }), "Calendly connected");
    if (r) { setToken(""); setState(r.data); setTypes(null); if (r.data?.webhook?.status === "error") toast.warning(r.data.webhook.error); }
  };
  const choose = async (uri) => { if (!uri) return; const r = await run(() => api.put("/me/calendly/event-type", { event_type_uri: uri }), "Recruiting event type saved"); if (r) setState((s) => ({ ...s, ...r.data })); };
  const test = async () => { const r = await run(() => api.post("/me/calendly/test")); if (r) { toast.success(`Connection OK · ${r.data.calendly_email}${r.data.event_type_active ? "" : " · event type inactive"}`); load(); } };
  const disconnect = async () => { if (await run(() => api.delete("/me/calendly"), "Calendly disconnected")) { setTypes(null); load(); } };

  const connected = state && state.status !== "not_connected";
  const hook = state?.webhook;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-900/40 sm:px-4" onClick={onClose} data-testid="calendly-sheet">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()} className="crm-root max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-slate-200 bg-white p-6 pb-safe shadow-2xl" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
        <div className="flex items-start justify-between">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0b1528] text-[#00C6EF]"><CalendarDays className="h-5 w-5" /></span>
          <button onClick={onClose} data-testid="calendly-close" className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"><X className="h-4 w-4" /></button>
        </div>
        <h2 className="mt-4 text-lg font-extrabold text-slate-900">Calendly Integration</h2>
        <p className="mt-1 text-sm text-slate-500">Recruits routed to you book their introductory interview on your calendar — without seeing your name. Bookings move them to <b>Interview Scheduled</b> automatically.</p>

        {!state && <p className="mt-6 text-center text-xs text-slate-400">Loading…</p>}

        {state && (
          <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-slate-500" data-testid="calendly-connection-status">
            <CalendlyBadge status={state.status} />
            {state.calendly_email && <span>{state.calendly_email}</span>}
            {state.last_sync_at && <span>· synced {relTime(state.last_sync_at)}</span>}
          </div>
        )}
        {state?.error && <p className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="calendly-error">{state.error}</p>}

        {state && !connected && (
          <form onSubmit={connect} className="mt-5 space-y-3" data-testid="calendly-connect-form">
            <ol className="list-decimal space-y-1 pl-5 text-xs text-slate-600">
              <li>In Calendly go to <b>Integrations &amp; apps → API &amp; webhooks</b> (or Profile → Developer) and click <b>Generate new token</b>.</li>
              <li>Give it a name like “Breeze CRM” and copy the token — Calendly shows it only once.</li>
              <li>Paste it below. It is stored on the server only and never sent to your browser again.</li>
            </ol>
            <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste your Calendly personal access token" data-testid="calendly-token-input" className={inputCls} autoComplete="off" />
            <button type="submit" disabled={busy || token.trim().length < 20} data-testid="calendly-connect-button" className={`${btnPrimary} w-full`}><Link2 className="h-4 w-4" /> {busy ? "Connecting…" : "Connect Calendly"}</button>
          </form>
        )}

        {connected && (
          <div className="mt-5 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-700">Recruiting interview event type</label>
              <select value={state.event_type_uri || ""} onChange={(e) => choose(e.target.value)} disabled={busy || types === null} data-testid="calendly-event-type-select" className={inputCls}>
                <option value="">{types === null ? "Loading your event types…" : types.length ? "Choose the event recruits should book…" : "No active event types found in Calendly"}</option>
                {(types || []).map((t) => <option key={t.uri} value={t.uri}>{t.name}{t.duration ? ` · ${t.duration} min` : ""}</option>)}
              </select>
              {state.event_type_url && <a href={state.event_type_url} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 hover:underline" data-testid="calendly-event-type-link"><ExternalLink className="h-3 w-3" /> Preview booking page</a>}
            </div>
            <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-[11px] ${hook?.active ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="calendly-webhook-status">
              {hook?.active ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
              <span>{hook?.active ? `Booking sync is on — Calendly notifies the CRM when recruits book, cancel or reschedule${hook.last_event_at ? ` (last event ${relTime(hook.last_event_at)})` : ""}.` : (state.webhook_error || hook?.error || "Booking sync is not active yet. Calendly webhooks need a Standard plan or higher — ask an admin to check the Calendly tab.")}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={test} disabled={busy} data-testid="calendly-test-button" className={btnSecondary}><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Test connection</button>
              <button onClick={disconnect} disabled={busy} data-testid="calendly-disconnect-button" className="inline-flex items-center gap-2 rounded-md border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50"><Unplug className="h-4 w-4" /> Disconnect</button>
            </div>
            <p className="text-[11px] text-slate-400">To reconnect with a different account, disconnect first and paste a new token.</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
